import {
  HostLinkContract,
  activatingTrainingId,
  isHostActivating,
  isHostAvailable,
  type HostStatusDocument,
} from '../protocol/hostLinkStatus';
import type { TokenRefusal } from '../protocol/tokenRefusal';

/** Severity for one status line/card - mirrors the Unity `StatusSeverity`. */
export type StatusSeverity = 'ok' | 'progress' | 'warning' | 'error';

/** Where this client stands with the training server it wants. */
export type ServerAvailabilityPhase =
  /** Nothing has been decided yet - the ordinary state before and during a first attempt. */
  | 'unknown'
  /** The last answer said we may start. */
  | 'available'
  /**
   * Refused `409 in_use`. The waiting window is open: a read-only status poll,
   * a Join button that enables when the answer changes, and - the whole point -
   * no further token request until a person presses.
   */
  | 'waiting'
  /** Refused for a reason waiting cannot fix (no gate, wrong gate, no answer). Nothing here frees up on its own. */
  | 'blocked';

/** One rendering of the availability state - headline, detail, and whether Join may be offered. */
export interface AvailabilityCard {
  /** A short line for a badge. */
  readonly headline: string;
  /** The sentence a person can act on. */
  readonly detail: string;
  /** Should a Join control be offered? A hint, never a reservation. */
  readonly joinEnabled: boolean;
  readonly severity: StatusSeverity;
}

/**
 * Plan-06 §9.6 / D24 - the waiting window, as pure state. Port of
 * `ServerAvailabilityModel.cs`.
 *
 * This class exists because the rule it encodes is easy to state and easy to
 * regress. A refused trainee **fails and stops**. While the server is busy the
 * client polls a read-only `GET /api/host/status` and NEVER re-attempts
 * `POST /api/token`, because polling the token endpoint would claim the slot
 * the instant it freed - retrying by another name, and a client racing a
 * person who is mid-training.
 *
 * So the rule lives here, engine-free, as two properties a test can hold to
 * it: `mayRequestToken` and `mayAutoConnect`. The transport asks; it does not
 * decide.
 *
 * **The enabled button is a hint, not a reservation.** If two people are
 * waiting and both press, one wins the claim and the other is refused again
 * and returns to waiting. That is correct, and the wording in `describe` says
 * so rather than papering over it.
 *
 * Two clocks, deliberately: elapsed time uses monotonic seconds (the caller's
 * `performance.now()/1000`) because cadence must not care what the wall clock
 * does; "started 12 minutes ago" uses UTC epoch ms, because that is a fact
 * about the other person's session.
 */
export class ServerAvailabilityModel {
  private waitStartedAt = 0;
  private lastPolledAt = 0;
  private readonly listeners = new Set<(model: ServerAvailabilityModel) => void>();

  private _phase: ServerAvailabilityPhase = 'unknown';
  private _lastRefusal: TokenRefusal | null = null;
  private _lastStatus: HostStatusDocument | null = null;
  private _lastPollFailure: string | null = null;
  private _joinEnabled = true;

  /** Raised whenever anything a surface renders has changed. Returns an unsubscribe. */
  onChanged(listener: (model: ServerAvailabilityModel) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private raiseChanged(): void {
    this.listeners.forEach((listener) => listener(this));
  }

  get phase(): ServerAvailabilityPhase {
    return this._phase;
  }

  /** The refusal that opened the current window; null when there is none. */
  get lastRefusal(): TokenRefusal | null {
    return this._lastRefusal;
  }

  /** The most recent status document; null until one is fetched. */
  get lastStatus(): HostStatusDocument | null {
    return this._lastStatus;
  }

  /** Why the last status poll produced no answer; null when the last one succeeded. */
  get lastPollFailure(): string | null {
    return this._lastPollFailure;
  }

  /** The waiting window is open (§9.6). */
  get isWaiting(): boolean {
    return this._phase === 'waiting';
  }

  /**
   * May a Join control be offered? Enabled only when the status has flipped
   * back to Online - the client cannot take the slot while it is held, so it
   * must not offer to (§9.6 step 4).
   */
  get joinEnabled(): boolean {
    return this._joinEnabled;
  }

  /**
   * D24's rule as a fact: no token request while the server is busy. True
   * outside the waiting window, and inside it only once the status says
   * Online - at which point the request comes from a person pressing Join,
   * never from this class.
   */
  get mayRequestToken(): boolean {
    return !this.isWaiting || this._joinEnabled;
  }

  /**
   * May the transport connect on its own? Never while waiting - not even with
   * the button enabled. The person chooses when to start; nothing starts on
   * their behalf, at a headset that may be sitting on a desk (§9.6 step 5).
   */
  get mayAutoConnect(): boolean {
    return !this.isWaiting;
  }

  /** When the session currently holding the cell started (epoch ms), as best this client knows. */
  get since(): number | null {
    return this._lastStatus?.since ?? this._lastRefusal?.inUse?.since ?? null;
  }

  /** How long this client has been waiting, in monotonic seconds. */
  waitElapsedSeconds(nowSeconds: number): number {
    return this.isWaiting ? Math.max(0, nowSeconds - this.waitStartedAt) : 0;
  }

  /**
   * §9.6 - ~5 s while somebody is actually standing there watching, backing
   * off to ~15 s after a couple of minutes. A waiting headset therefore costs
   * a few hundred requests an hour against a 100,000/day allowance.
   */
  nextPollDelaySeconds(nowSeconds: number): number {
    return this.waitElapsedSeconds(nowSeconds) >= HostLinkContract.statusPollBackoffAfterSeconds
      ? HostLinkContract.statusPollSlowSeconds
      : HostLinkContract.statusPollFastSeconds;
  }

  /** Is a status poll due? False outside the waiting window - nothing else polls. */
  shouldPollStatus(nowSeconds: number): boolean {
    return this.isWaiting && nowSeconds - this.lastPolledAt >= this.nextPollDelaySeconds(nowSeconds);
  }

  /** A poll has been dispatched (call before awaiting it, so one request is in flight at a time). */
  markPolled(nowSeconds: number): void {
    this.lastPolledAt = nowSeconds;
  }

  /** The token request was refused. */
  onRefused(refusal: TokenRefusal | null, nowSeconds: number): void {
    if (!refusal) return;

    this._lastRefusal = refusal;
    this._lastPollFailure = null;

    if (refusal.kind === 'in_use') {
      // One attempt, one answer. The window opens here and nothing reopens
      // the token path until a person presses Join.
      this._phase = 'waiting';
      this.waitStartedAt = nowSeconds;

      // The refusal IS this moment's status answer, so the first poll is one
      // cadence away rather than immediate.
      this.lastPolledAt = nowSeconds;
      this._lastStatus = null;
      this._joinEnabled = false;
    } else {
      this._phase = 'blocked';

      // Nothing about a missing or stale gate is helped by disabling a
      // control: the fix is at the other end, and a retry is how the client
      // notices it landed.
      this._joinEnabled = true;
    }

    this.raiseChanged();
  }

  /** A status document arrived from `GET /api/host/status`. */
  onStatus(document: HostStatusDocument | null, nowSeconds: number): void {
    if (!document) return;

    this.lastPolledAt = nowSeconds;
    this._lastStatus = document;
    this._lastPollFailure = null;

    if (this.isWaiting) {
      // The one condition that enables the button. Anything else - in_use,
      // offline, error, a version this client cannot read - leaves it disabled.
      this._joinEnabled = isHostAvailable(document);
    } else if (this._phase !== 'blocked' && isHostAvailable(document)) {
      this._phase = 'available';
    }

    this.raiseChanged();
  }

  /**
   * The status poll produced no usable answer. The button stays disabled:
   * "I could not check" is not "it is free" - D20's cardinal rule, on the
   * client side of the same question.
   */
  onStatusUnavailable(reason: string | null, nowSeconds: number): void {
    this.lastPolledAt = nowSeconds;
    this._lastPollFailure = reason || 'no answer';
    if (this.isWaiting) this._joinEnabled = false;
    this.raiseChanged();
  }

  /**
   * The status route does not exist on this deployment (no
   * `/api/host/status` - a client newer than the cloud it is talking to). The
   * waiting window cannot do its job, so it stops pretending to: the button
   * enables and the card says the check is unavailable.
   *
   * This is not a hole in D24. What D24 forbids is a client RETRYING on its
   * own; a person pressing a button that says "I could not check" is an
   * ordinary first attempt, and the claim is still the only authority.
   */
  onStatusUnsupported(reason: string | null, nowSeconds: number): void {
    this.lastPolledAt = nowSeconds;
    this._lastPollFailure = reason || 'status endpoint unavailable';
    if (this.isWaiting) {
      this._phase = 'blocked';
      this._joinEnabled = true;
    }
    this.raiseChanged();
  }

  /** A person pressed Join. Closes the window and allows exactly one ordinary attempt. */
  onJoinRequested(): void {
    if (this._phase === 'unknown' && this._lastRefusal === null) return;
    this._phase = 'unknown';
    this._lastRefusal = null;
    this._lastPollFailure = null;
    this._joinEnabled = true;
    this.raiseChanged();
  }

  /** The room connected - whatever was refused is behind us. */
  onConnected(): void {
    if (this._phase === 'available' && this._lastRefusal === null) return;
    this._phase = 'available';
    this._lastRefusal = null;
    this._lastPollFailure = null;
    this._joinEnabled = true;
    this.raiseChanged();
  }

  /**
   * The card a surface renders. All user-facing wording lives here, in a pure
   * method, so the sentences a refused trainee reads are covered by unit tests
   * rather than by looking at a headset.
   * @param nowUtcMs Epoch ms "now" for the "started N minutes ago" sentence.
   */
  describe(nowUtcMs: number): AvailabilityCard {
    switch (this._phase) {
      case 'waiting':
        return this.describeWaiting(nowUtcMs);
      case 'blocked':
        return this.describeBlocked();
      case 'available':
        return { headline: 'available', detail: 'The training server is ready.', joinEnabled: true, severity: 'ok' };
      default:
        // Deliberately ok, not progress: nothing has gone wrong, and a status
        // surface that headlines "checking" before anything has happened
        // buries the field that is actually speaking.
        return { headline: 'checking', detail: 'Checking the training server.', joinEnabled: true, severity: 'ok' };
    }
  }

  private describeWaiting(nowUtcMs: number): AvailabilityCard {
    if (this._joinEnabled) {
      // Deliberately says the race out loud. A button that promised a
      // reservation would be lying: the claim is the only authority.
      return {
        headline: 'server free',
        detail:
          'The training server is free. Press Join to start - if someone else is waiting too, whoever presses first gets it.',
        joinEnabled: true,
        severity: 'ok',
      };
    }

    const status = this._lastStatus;

    if (status?.status === 'offline') {
      return {
        headline: 'server offline',
        detail: 'The training server is not running, so it cannot be started from here. Ask an admin to start it.',
        joinEnabled: false,
        severity: 'error',
      };
    }

    if (status?.status === 'error') {
      const reason = status.reason || 'unspecified';
      const detail = status.detail ? ` ${status.detail}` : '';
      return {
        headline: 'server error',
        detail: `The training server reports a problem (${reason}).${detail}`,
        joinEnabled: false,
        severity: 'error',
      };
    }

    // §10.3: a rebuild holds the claim too, and it is a wait of a very
    // different length and character - saying "in use" for it would be true
    // and useless.
    const activating =
      (status !== null && isHostActivating(status)) || this._lastRefusal?.inUse?.kind === 'activation';
    if (activating) {
      const training = status ? activatingTrainingId(status) : '';
      const named = training ? `training ${training}` : 'a training';
      return {
        headline: 'server preparing',
        detail: `The training server is preparing ${named}. It will be free shortly - the Join button enables by itself when it is.`,
        joinEnabled: false,
        severity: 'progress',
      };
    }

    let text =
      'This training server is already in use.' +
      this.sinceSentence(nowUtcMs) +
      ' Wait for it to finish, or use another cell.';

    if (this._lastPollFailure) {
      // Say that the last check failed rather than silently showing a stale
      // answer as if it were current.
      text += ` (Could not re-check just now: ${this._lastPollFailure}.)`;
    }

    return { headline: 'in use', detail: text, joinEnabled: false, severity: 'warning' };
  }

  /**
   * " A session started 12 minutes ago." - the sentence that lets a person
   * decide whether to wait or walk away, which is the whole job of this screen
   * (§9.6 step 2). Empty when the answer is genuinely unknown; an invented one
   * would be worse than none.
   */
  private sinceSentence(nowUtcMs: number): string {
    const since = this.since;
    if (since === null) return '';

    const device = this._lastStatus?.claim?.device ?? this._lastRefusal?.inUse?.device ?? null;
    const elapsed = describeElapsed(nowUtcMs - since);

    return device ? ` A session started ${elapsed} on ${device}.` : ` A session started ${elapsed}.`;
  }

  private describeBlocked(): AvailabilityCard {
    const refusal = this._lastRefusal;
    const kind = refusal?.kind ?? 'rejected';
    const says = refusal?.serverMessage ? ` The server says: ${refusal.serverMessage}` : '';

    switch (kind) {
      case 'in_use':
        // Only reachable via onStatusUnsupported: refused, and then the
        // deployment turned out to have no status route to watch. Say both
        // facts - a silent enabled button after a refusal would look like the
        // refusal never happened.
        return {
          headline: 'in use',
          detail:
            'This training server was already in use, and this deployment cannot report when it frees up' +
            (this._lastPollFailure ? ` (${this._lastPollFailure})` : '') +
            '. Press Join to try again, or use another cell.',
          joinEnabled: true,
          severity: 'warning',
        };

      case 'not_configured':
        // A deployment problem, not a user one. Naming it as such is what
        // stops someone hunting for a headset fault.
        return {
          headline: 'not configured',
          detail:
            'This deployment has no wearer access configured (503), so no headset can join yet. ' +
            'The vision server publishes it when it starts - check the server is running.' +
            says,
          joinEnabled: true,
          severity: 'error',
        };

      case 'unauthorized':
        // D21: the gate is minted per run, so a restarted server is the usual
        // cause. Worth saying, because "reconnect" is then the fix rather
        // than a guess.
        return {
          headline: 'access refused',
          detail:
            "The training server refused this headset's access code (401). " +
            'The code changes each time the server restarts - reconnect to pick up the new one, or ask an admin.' +
            says,
          joinEnabled: true,
          severity: 'error',
        };

      case 'unreachable':
        return {
          headline: 'unreachable',
          detail:
            'Could not reach the training service' +
            (refusal?.transportError ? ` (${refusal.transportError})` : '') +
            ". Check the headset's network.",
          joinEnabled: true,
          severity: 'error',
        };

      default: {
        const status = refusal && refusal.httpStatus > 0 ? ` (${refusal.httpStatus})` : '';
        return {
          headline: 'refused',
          detail: `The training service refused this headset${status}.` + says,
          joinEnabled: true,
          severity: 'error',
        };
      }
    }
  }
}

/**
 * Elapsed time in the words a person waiting outside a training bay would use.
 * Coarse on purpose: the decision this informs is "wait or walk away", and it
 * needs a minute, not a second.
 * @param elapsedMs Elapsed milliseconds; negative values (clock skew between a
 * headset and the cloud) read as "moments ago" - the honest reading of a
 * session that appears to start in the future.
 */
export function describeElapsed(elapsedMs: number): string {
  const clamped = Math.max(0, elapsedMs);
  const minutes = Math.floor(clamped / 60000);
  if (minutes < 1) return 'moments ago';
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hourText = hours === 1 ? '1 hour' : `${hours} hours`;
  if (rest === 0) return `${hourText} ago`;
  const minuteText = rest === 1 ? '1 minute' : `${rest} minutes`;
  return `${hourText} ${minuteText} ago`;
}
